import { formatCurrency, formatQuantity, formatRate, formatPercentage } from '@/utils/formatters';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Calendar, Search, ChevronDown, ChevronUp, Filter, RefreshCw } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'dd MMM yyyy');
  } catch {
    return '-';
  }
};

export function GRNRegisterContent() {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const pageSize = 15;
  const totalPages = Math.ceil(totalEntries / pageSize);

  // Filters
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    supplierSearch: '',
    poSearch: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({});

  const fetchEntries = useCallback(async (page = currentPage, filterParams = appliedFilters) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: pageSize.toString() });
      
      if (filterParams.dateFrom) params.append('date_from', filterParams.dateFrom);
      if (filterParams.dateTo) params.append('date_to', filterParams.dateTo);
      if (filterParams.supplierSearch) params.append('supplier_search', filterParams.supplierSearch);
      if (filterParams.poSearch) params.append('po_search', filterParams.poSearch);

      const [entriesRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/store/purchase-entries?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/store/purchase-entries/count?${params}`, { headers: getAuthHeader() })
      ]);

      setEntries(entriesRes.data);
      setTotalEntries(countRes.data.total);
    } catch (error) {
      toast.error('Failed to fetch GRN entries');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, appliedFilters]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchEntries(page, appliedFilters);
  };

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1);
    fetchEntries(1, filters);
  };

  const clearFilters = () => {
    const emptyFilters = { dateFrom: '', dateTo: '', supplierSearch: '', poSearch: '' };
    setFilters(emptyFilters);
    setAppliedFilters({});
    setCurrentPage(1);
    fetchEntries(1, {});
  };

  const hasActiveFilters = Object.values(appliedFilters).some(v => v);

  return (
      <div className="space-y-4 pb-8" data-testid="grn-register-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">GRN Register</h1>
            <p className="text-sm text-muted-foreground">Goods Received Notes with line items and charges</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchEntries(currentPage, appliedFilters)} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  <span className="font-medium">Filters</span>
                  {hasActiveFilters && (
                    <Badge variant="secondary" className="ml-2">Active</Badge>
                  )}
                </div>
                {filtersOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 pb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">From Date</Label>
                    <Input
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">To Date</Label>
                    <Input
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Supplier</Label>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search supplier..."
                        value={filters.supplierSearch}
                        onChange={(e) => setFilters({ ...filters, supplierSearch: e.target.value })}
                        className="h-9 pl-8"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">PO Number</Label>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search PO..."
                        value={filters.poSearch}
                        onChange={(e) => setFilters({ ...filters, poSearch: e.target.value })}
                        className="h-9 pl-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" onClick={applyFilters}>Apply Filters</Button>
                  <Button size="sm" variant="outline" onClick={clearFilters}>Clear</Button>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Top Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1">
            <Button variant="outline" size="sm" onClick={() => handlePageChange(1)} disabled={currentPage === 1 || isLoading}>First</Button>
            <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading}>Prev</Button>
            <span className="px-3 text-sm text-muted-foreground">Page {currentPage}/{totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading}>Next</Button>
            <Button variant="outline" size="sm" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || isLoading}>Last</Button>
          </div>
        )}

        {/* GRN Entries */}
        <div className="space-y-0">
          {isLoading ? (
            <Card>
              <CardContent className="py-16 text-center">
                <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground">Loading GRN entries...</p>
              </CardContent>
            </Card>
          ) : entries.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
                <p className="text-muted-foreground">No GRN entries found</p>
                {hasActiveFilters && (
                  <Button variant="link" onClick={clearFilters} className="mt-2">
                    Clear filters
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            entries.map((entry, index) => (
              <GRNEntryCard key={entry.id} entry={entry} isEven={index % 2 === 0} />
            ))
          )}
        </div>

        {/* Pagination */}
        <div className="flex flex-col items-center gap-2 pb-16">
          <p className="text-xs text-muted-foreground">
            {isLoading ? 'Loading...' : `Showing ${entries.length} of ${totalEntries} GRN entries`}
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={() => handlePageChange(1)} disabled={currentPage === 1 || isLoading}>
                First
              </Button>
              <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading}>
                Prev
              </Button>
              <span className="px-3 text-sm text-muted-foreground">Page {currentPage}/{totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading}>
                Next
              </Button>
              <Button variant="outline" size="sm" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || isLoading}>
                Last
              </Button>
            </div>
          )}
        </div>
      </div>
  );
}

// Standalone page wrapper
export default function GRNRegisterPage() {
  return (
    <DashboardLayout>
      <GRNRegisterContent />
    </DashboardLayout>
  );
}

function GRNEntryCard({ entry, isEven }) {
  // Alternating light pink and light blue backgrounds
  const bgClass = isEven ? 'bg-pink-50' : 'bg-blue-50';
  const headerBgClass = isEven ? 'bg-pink-100/70' : 'bg-blue-100/70';
  const footerBgClass = isEven ? 'bg-pink-100/50' : 'bg-blue-100/50';
  
  return (
    <div className={`${bgClass} border-x border-t last:border-b first:rounded-t-lg last:rounded-b-lg`} data-testid={`grn-entry-${entry.id}`}>
      {/* GRN Header */}
      <div className={`px-4 py-3 border-b ${headerBgClass}`}>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div>
            <span className="text-xs text-muted-foreground">GRN No.</span>
            <p className="font-mono font-medium text-sm">{entry.store_receipt_no || '-'}</p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Date</span>
            <p className="text-sm">{formatDate(entry.purchase_entry_timestamp || entry.created_at)}</p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Supplier</span>
            <p className="text-sm font-medium">{entry.supplier_name || '-'}</p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Invoice/DC</span>
            <p className="text-sm">{entry.invoice_no || entry.invoice_dc_no || '-'}</p>
          </div>
          {entry.po_number && (
            <div>
              <span className="text-xs text-muted-foreground">Header PO</span>
              <Badge variant="outline" className="font-mono text-xs">{entry.po_number}</Badge>
            </div>
          )}
        </div>
      </div>

      {/* Line Items Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">Item Name</th>
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">PO No.</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Qty</th>
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">Unit</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Rate</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Amount</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Addl. Charges</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Total</th>
            </tr>
          </thead>
          <tbody>
            {(entry.items || []).map((item, idx) => (
              <tr key={idx} className="border-b last:border-b-0">
                <td className="px-4 py-2 font-medium">{item.item_name}</td>
                <td className="px-4 py-2">
                  {item.po_number ? (
                    <Badge variant="outline" className="font-mono text-xs">{item.po_number}</Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right font-mono">{formatCurrency(item.supplier_qty)}</td>
                <td className="px-4 py-2 text-muted-foreground">{item.supplier_unit || '-'}</td>
                <td className="px-4 py-2 text-right font-mono">{formatCurrency(item.supplier_rate)}</td>
                <td className="px-4 py-2 text-right font-mono">{formatCurrency(item.supplier_amount)}</td>
                <td className="px-4 py-2 text-right font-mono text-muted-foreground">
                  {item.additional_charges ? formatCurrency(item.additional_charges) : '-'}
                </td>
                <td className="px-4 py-2 text-right font-mono font-medium">
                  {formatCurrency(item.system_amount_with_charges || item.supplier_amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* GRN Footer - Totals */}
      <div className={`px-4 py-3 border-t ${footerBgClass}`}>
        <div className="flex flex-wrap justify-end gap-x-8 gap-y-1 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Subtotal:</span>
            <span className="font-mono">{formatCurrency(entry.total_supplier_amount)}</span>
          </div>
          {entry.other_charges_taxable > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Other Charges (Taxable):</span>
              <span className="font-mono">{formatCurrency(entry.other_charges_taxable)}</span>
            </div>
          )}
          {entry.gst_amount > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">GST @ {entry.gst_rate || 0}%:</span>
              <span className="font-mono">{formatCurrency(entry.gst_amount)}</span>
            </div>
          )}
          {entry.other_charges_non_taxable > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Other Charges (Non-taxable):</span>
              <span className="font-mono">{formatCurrency(entry.other_charges_non_taxable)}</span>
            </div>
          )}
          <div className="flex items-center gap-2 font-semibold">
            <span>Grand Total:</span>
            <span className="font-mono text-primary">{formatCurrency(entry.grand_total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
